import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Header } from '../components/layout/Header'
import { Footer } from '../components/layout/Footer'
import { PaymentSetupError } from './PaymentSetupError'
import type { PaymentSetupErrorState } from './payment-setup-error-code'

// Thin web -> edge-function hop for the payment-setup SMS link (issue #206).
// The SMS carries a STABLE, signed token URL that renders this page. We hand the
// browser off to the public `payment-setup` edge function with a full-page
// navigation (NOT a fetch): the function verifies the token, mints a FRESH
// Stripe onboarding link, and 302s the browser to Stripe. A fetch could not
// follow that cross-origin redirect, so we must navigate the top-level document.
//
// The function's `refresh_url` points back at THIS same token URL, so an expired
// mid-onboarding link self-heals by re-entering here and re-minting.
export function PaymentSetupRedirect() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [failed, setFailed] = useState(false)
  // Which branded error page to show, as the COARSE client-facing state (the
  // granular reason never reaches the browser — see PaymentSetupError). A missing
  // token is recoverable (a fresh link fixes it); a missing VITE_SUPABASE_URL is
  // our own outage — non_recoverable, which also correctly hides the resend form,
  // since we could not reach the resend endpoint either.
  const [failureState, setFailureState] =
    useState<PaymentSetupErrorState>('recoverable')

  useEffect(() => {
    if (!token) {
      setFailureState('recoverable')
      setFailed(true)
      return
    }
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
    if (!supabaseUrl) {
      setFailureState('non_recoverable')
      setFailed(true)
      return
    }
    const target = `${supabaseUrl}/functions/v1/payment-setup?token=${encodeURIComponent(token)}`
    window.location.replace(target)
  }, [token])

  // The dead-end error state is gone (#209): the missing-token case now renders
  // the SAME branded page the edge function's own failures 302 to — the one that
  // can text the organizer a fresh link — instead of a "contact us" cul-de-sac.
  if (failed) return <PaymentSetupError state={failureState} />

  return (
    <div className="min-h-screen flex flex-col">
      <Header variant="light" />
      <div className="container mx-auto px-4 py-16 flex-1 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <div
            className="mx-auto mb-6 h-10 w-10 animate-spin rounded-full border-4 border-[var(--color-border)] border-t-[var(--color-action-primary)]"
            role="status"
            aria-label="Redirecting"
          />
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)] mb-2">
            Taking you to secure payment setup…
          </h1>
          <p className="text-[var(--color-text-secondary)]">
            One moment while we connect you to our payments partner, Stripe.
          </p>
        </div>
      </div>
      <Footer />
    </div>
  )
}
